import { gql, useQuery } from "@apollo/client";
import { Box } from "@chakra-ui/react";
import { Tree } from "antd";
import type { DataNode, TreeProps } from "antd/es/tree";
import * as React from "react";
import { useHistory } from "react-router-dom";

interface Props {
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  values: any;
}

const ProductTreeView: React.FC<Props> = ({ values, setFieldValue }) => {
  const history = useHistory();
  const { refetch: brandRefetch, data: brandData } = useQuery(
    BRANDS_PRODUCTS_QUERY,
    {}
  );
  const { refetch: categoryRefetch, data: CategoryData } = useQuery(
    CATEGORY_PRODUCTS_QUERY,
    {}
  );
  const [productTree, setProductTree] = React.useState<DataNode[]>([]);
  const [selectedProducts, setSelectedProducts] = React.useState<string[]>(
    values.products
  );
  const [loading, setLoading] = React.useState<boolean>(false);
  React.useEffect(() => {
    setLoading(true);
    if (values.type === "brand" && brandData) {
      const treeData: DataNode[] = brandData.brandsConnection.values.map(
        (brand: any) => {
          return {
            title: brand.brandName,
            key: "B" + brand.id,
            children: brand.products.map((product: any) => {
              return {
                title: product.productName,
                key: product.id.toString(),
              };
            }),
          };
        }
      );
      setProductTree(treeData);
    }
    if (values.type === "category" && CategoryData) {
      const treeData: DataNode[] = CategoryData.categoriesConnection.values.map(
        (category: any) => {
          return {
            title: category.categoryName,
            key: "C" + category.id,
            children: [
              ...category.products.map((product: any) => {
                return {
                  title: product.productName,
                  key: product.id.toString(),
                };
              }),
              // products whose extra category is this one; tree keys must be unique
              ...(category.extra_products || []).map((product: any) => {
                return {
                  title: product.productName,
                  key: `X${category.id}_${product.id}`,
                };
              }),
            ],
          };
        }
      );
      setProductTree(treeData);
    }
    setLoading(false);
  }, [brandData, CategoryData, values.type]);

  const onSelect: TreeProps["onSelect"] = (selectedKeys, info) => {
    console.log("selected", selectedKeys, info);
  };

  const onCheck: TreeProps["onCheck"] = (checkedKeys, info) => {
    console.log("onCheck", checkedKeys, info);
    const checkKeys = checkedKeys as string[];
    const ids = new Set(
      checkKeys
        .map((key) => key.toString())
        .filter((key) => key[0] !== "B" && key[0] !== "C")
        .map(toProductId)
    );
    // the same product can sit under several categories, so unchecking one copy removes it everywhere
    if (!info.checked) {
      const nodes = info.node.children ? info.node.children : [info.node];
      nodes.forEach((node) => ids.delete(toProductId(node.key.toString())));
    }
    const productsSet = Array.from(ids);
    setSelectedProducts(productsSet);
    setFieldValue("products", productsSet);
  };

  // show every copy of a selected product as checked
  const checkedKeys = [
    ...selectedProducts,
    ...productTree.flatMap((parent) =>
      (parent.children || [])
        .map((node) => node.key.toString())
        .filter(
          (key) => key[0] === "X" && selectedProducts.includes(toProductId(key))
        )
    ),
  ];
  if (loading) return <Box>Loading...</Box>;
  else
    return (
      <Box my="3">
        {/* {selectedProducts} */}
        <Tree
          checkable
          // defaultExpandedKeys={["0-0-0", "0-0-1"]}
          // defaultSelectedKeys={["0-0-0", "0-0-1"]}
          checkedKeys={checkedKeys}
          onSelect={onSelect}
          onCheck={onCheck}
          treeData={productTree}
          // checkedKeys={selectedProducts}
        />
      </Box>
    );
};

export default ProductTreeView;

// "X{categoryId}_{productId}" -> "{productId}"
const toProductId = (key: string) => (key[0] === "X" ? key.split("_")[1] : key);

export const BRANDS_PRODUCTS_QUERY = gql`
  query BRANDS_QUERY($sort: String, $limit: Int, $start: Int, $where: JSON) {
    brandsConnection(start: $start, limit: $limit, sort: $sort, where: $where) {
      aggregate {
        totalCount
      }
      values {
        id
        brandName
        products {
          id
          productName
        }
      }
    }
  }
`;

export const CATEGORY_PRODUCTS_QUERY = gql`
  query CATEGORY_PRODUCTS_QUERY(
    $sort: String
    $limit: Int
    $start: Int
    $where: JSON
  ) {
    categoriesConnection(
      start: $start
      limit: $limit
      sort: $sort
      where: $where
    ) {
      aggregate {
        totalCount
      }
      values {
        id
        categoryName
        products {
          id
          productName
        }
        extra_products {
          id
          productName
        }
      }
    }
  }
`;
